import { Box, Typography, Paper, Tooltip, IconButton, Skeleton } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import moment from "moment-timezone";
import { useSelector, useDispatch } from "react-redux";
import { useEffect } from "react";
import { tripData, tripApproving } from "store/tripSlice";
import { tripTimeline, getTimeline } from "store/vehicleTripSlice";
import { GetTimeZoneFromAbbreviation } from "common/Constants";

const TripSummary = () => {
  const dispatch = useDispatch();
  const trip = useSelector(tripData);
  const approving = useSelector(tripApproving);
  const events = useSelector(tripTimeline);

  // Load timeline when trip is loaded
  useEffect(() => {
    if (trip?.id) {
      dispatch(getTimeline({ tripNumber: trip.id.toString(), isActive: "active" }));
    }
  }, [trip?.id, dispatch]);

  // Calculate statistics from events
  const calculateEventStats = () => {
    if (!events || events.length === 0) {
      return {
        totalMiles: 0,
        totalMinutes: 0,
      };
    }

    const activeEvents = events.filter(event => event.isActive !== false);

    const totalMiles = activeEvents.reduce((sum, event) => sum + (event.eventDriveTimeMiles || 0), 0);
    const totalMinutes = activeEvents.reduce((sum, event) => sum + (event.eventDriveTimeMinutes || 0), 0);

    return {
      totalMiles: Math.trunc(totalMiles),
      totalMinutes: Math.round(totalMinutes),
    };
  };

  const stats = calculateEventStats();

  const formatTime = (dateTime: any) => {
    if (!dateTime) return "N/A";
    const tzAbbrev = trip?.hubTimeZone?.label || "UTC";
    // Display as received without conversion, just format it
    return `${moment(dateTime).format("MM/DD/YY HH:mm")} ${tzAbbrev}`;
  };

  const formatMinutes = (mins: number) => {
    if (!mins || mins === 0) return "0m";
    const roundedMins = Math.round(mins);
    if (roundedMins >= 60) {
      const hours = Math.floor(roundedMins / 60);
      const minutes = roundedMins % 60;
      return `${hours}h ${minutes}m`;
    }
    return `${roundedMins}m`;
  };

  // Calculated ASAP: true when the earliest feasible arrival (call time + drive time + 60 min lead)
  // lands after the requested pickup, i.e. the trip can't realistically make its pickup.
  const calcAsap = () => {
    const driveTime = Number(trip?.driveTime);
    const pickup = trip?.origPickupDateTime ?? trip?.pickupDateTime;
    if (!trip?.callDateTime || isNaN(driveTime) || !pickup) return "N/A";
    const earliestArrival = moment(trip.callDateTime).add(driveTime + 60, "minutes");
    return earliestArrival.isAfter(moment(pickup)) ? "True" : "False";
  };

  // Desktop keeps the dense fixed 6-column layout. On smaller screens the rigid
  // grid truncates every value with an ellipsis (unreadable on a phone), so we
  // collapse to a single column on xs and 2/3 columns on sm/md and let values wrap.
  const SummaryRow = ({ children }) => (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, minmax(0, 1fr))",
        md: "repeat(3, minmax(0, 1fr))",
        // The dense 6-column layout needs real width. With the sidenav open, xl
        // (1200) leaves only ~960px of content and the rightmost columns collapsed
        // to ~1 char wide. Reserve 6 columns for xxl (1400+); lg/xl get a roomier
        // 4 columns. Even, shrinkable minmax(0,1fr) tracks keep long values (names,
        // emails) wrapping normally instead of one character per line — the old
        // uneven template starved the last column that held the longest values.
        lg: "repeat(4, minmax(0, 1fr))",
        xl: "repeat(4, minmax(0, 1fr))",
        xxl: "repeat(6, minmax(0, 1fr))",
      },
      gap: 1,
      mb: 0.5,
      alignItems: "center"
    }}>
      {children}
    </Box>
  );

  const SummaryField = ({ label, value, info, loading }: { label: any; value: any; info?: string; loading?: boolean }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", minWidth: 0 }}>
      <Typography component="span" sx={{ fontWeight: 600, mr: 0.5, fontSize: "0.85rem", whiteSpace: "nowrap", flexShrink: 0 }}>
        {label}
        {info && (
          <Tooltip title={info}>
            <IconButton size="small" sx={{ p: 0, mx: 0.25 }}>
              <InfoIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
        :
      </Typography>
      <Typography
        component="span"
        sx={{
          // Always wrap long values so they're never clipped at narrow/half
          // widths; at full desktop width they still sit on a single line.
          fontSize: "0.85rem",
          whiteSpace: "normal",
          overflow: "visible",
          wordBreak: "break-word",
          minWidth: 0,
        }}
        title={loading ? "" : value}
      >
        {loading ? <Skeleton variant="text" width={110} sx={{ display: "inline-block" }} /> : value}
      </Typography>
    </Box>
  );

  if (!trip) return null;

  return (
    <Paper sx={{ pt: 1, pb: 1, pl: 2, pr: 1, m: 1, backgroundColor: "#f5f5f5" }}>
      {/* Row 1: Client | Hub | Call Time | Driver | OTP Status | Approved By */}
      <SummaryRow>
        <SummaryField label="Client" value={trip.clientName || "N/A"} />
        <SummaryField label="Hub" value={trip.hub?.name || "N/A"} />
        <SummaryField label="Call Time" value={formatTime(trip.callDateTime)} />
        <SummaryField label="Driver" value={trip.driver?.name ? `${trip.driver.name}${trip.driver.hub?.name ? ` (${trip.driver.hub.name})` : ""}` : "N/A"} />
        <SummaryField label="OTP" value={trip.onTimePerformanceStatus || "N/A"} />
        <SummaryField
          label="Approved By"
          loading={approving}
          value={trip.tripReview?.approvedBy ? `${trip.tripReview.approvedBy} ${trip.tripReview.approvedDate ? moment(trip.tripReview.approvedDate).format("MM/DD/YY") : ""}`.trim() : "N/A"}
        />
      </SummaryRow>

      {/* Row 2: Trip # | Pickup Location | Requested Time | Driver Phone # | Total Miles | Closed By */}
      <SummaryRow>
        <SummaryField label="Trip #" value={trip.id || "N/A"} />
        <SummaryField label="Pickup" value={trip.pickupLocation?.name || "N/A"} />
        <SummaryField label="Req. Time" value={formatTime(trip.origPickupDateTime)} />
        <SummaryField label="Driver Ph" value={trip.driver?.phone || "N/A"} />
        <SummaryField label="Miles" value={stats.totalMiles === 0 ? "--" : stats.totalMiles.toLocaleString()} />
        <SummaryField label="Closed By" value={trip.tripReview?.closedBy || "N/A"} />
      </SummaryRow>

      {/* Row 3: Control # | Drop Off Location | Contractual Pickup Time | Van # | Total Minutes | (empty) */}
      <SummaryRow>
        <SummaryField label="Control #" value={trip.controlNumber || "N/A"} />
        <SummaryField label="Drop Off" value={trip.dropoffLocation?.name || "N/A"} />
        <SummaryField label="Contract Time" value={formatTime(trip.contractualPickupTime)} />
        <SummaryField label="Van #" value={trip.vehicle?.unitNumber || "N/A"} />
        <SummaryField label="Minutes" value={formatMinutes(stats.totalMinutes)} />
        <Box />
      </SummaryRow>

      {/* Row 4: Short Key | Trip Type | Requestor Name */}
      <SummaryRow>
        <SummaryField label="Short Key" value={trip.shortKey || "N/A"} />
        <SummaryField label="Trip Type" value={trip.tripType?.name || "N/A"} />
        <SummaryField label="Requestor" value={trip.requesterName || "N/A"} />
        <Box />
        <SummaryField label="Calc ASAP" value={calcAsap()} info="This ASAP is calculated by RCX for the purposes of OTP metrics, and different than the client provided ASAP field" />
        <Box />
      </SummaryRow>

      {/* Row 5: (empty) | Assist Min */}
      <SummaryRow>
        <Box />
        <SummaryField label="Assist Min" value={formatMinutes(trip.assistMinutes || 0)} />
        <Box />
        <Box />
        <Box />
        <Box />
      </SummaryRow>

      {/* Comments - full width */}
      <Box sx={{ display: "flex", mt: 1 }}>
        <Typography component="span" sx={{ fontWeight: 600, mr: 0.5, fontSize: "0.85rem", whiteSpace: "nowrap", flexShrink: 0 }}>
          Comments:
        </Typography>
        <Typography component="span" sx={{ fontSize: "0.85rem" }}>{trip.comments || "N/A"}</Typography>
      </Box>

      {/* Dispatcher Notes - full width, one line per note */}
      <Box sx={{ mt: 0.5 }}>
        <Typography component="span" sx={{ fontWeight: 600, mr: 0.5, fontSize: "0.85rem" }}>
          Dispatcher Notes:
        </Typography>
        {trip.dispatcherNotes ? (
          <Box sx={{ ml: 1, mt: 0.25 }}>
            {trip.dispatcherNotes
              .split(/(?=\[\d{1,2}\/\d{1,2}\/\d{2,4})/)
              .filter(note => note.trim())
              .map((note, idx) => (
                <Typography key={idx} sx={{ fontSize: "0.85rem" }}>{note.trim()}</Typography>
              ))}
          </Box>
        ) : (
          <Typography component="span" sx={{ fontSize: "0.85rem" }}>N/A</Typography>
        )}
      </Box>
    </Paper>
  );
};

export default TripSummary;
